import { computed, makeObservable, observable } from "mobx";
import client from "./../feathers";
import axios, { AxiosRequestConfig } from 'axios';
import { Service } from '@feathersjs/feathers';

import { Data } from "./data";
import { IFile, DataType, FileType, Services } from "./../types";
import { rootStore } from "src/app";

export class File extends Data<IFile> implements IFile {
  title: string   = null;
  mime:  string   = null;
  type:  FileType = null;
  size:  number   = null;
  key?:  string   = null;
  uri:   string | ArrayBuffer = null;
  item_id?:  number  = null;
  room_id?:  number  = null;
  progress:  number  = 0;      // Up-/downloadload progress
  uploading: boolean = false;

  protected storage_service: Service<{url: string}>;

  protected constructor(data: IFile) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.File, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      uri:        observable,
      key:        observable,
      title:      observable,
      mime:       observable,
      type:       observable,
      size:       observable,
      uploadedBy: computed,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
    this.storage_service = client.service(Services.Storage);
  }

  public async delete(): Promise<boolean> {
    const fileDeleted = client.service('file').remove(this.id);
    return fileDeleted;
  }

  public static create(data?: IFile): File {
    const fileData = {
      ...data,
      key: data.key,
      uri: data.uri,
      title: data.title || null,
      type: data.type || null,
      size: data.size || null,
      ...Data.create(data),
    };
    return new File(fileData);
  }

  protected fromData(data: IFile): void {
    super.fromData(data);
    this.key          = data.key;
    this.uri          = data.uri;
    this.title        = data.title;
    this.type         = data.type;
    this.mime         = data.mime;
    this.size         = data.size;
    this.item_id      = data.item_id;
    this.workspace_id = data.workspace_id;
    this.room_id      = data.room_id;
  }

  public toData(): any {
    return {
      ...super.toData(),
      key:     this.key,
      room_id: this.room_id,
      item_id: this.item_id,
      title:   this.title,
      type:    this.type,
      mime:    this.mime,
      size:    this.size,
    };
  }

  protected toPatchData(patch: Partial<IFile>): any {
    return {
      ...super.toPatchData(patch),
      title: "title" in patch ? patch.title : undefined,
      type:  "type"  in patch ? patch.type  : undefined,
      mime:  "mime"  in patch ? patch.mime  : undefined,
      size:  "size"  in patch ? patch.size  : undefined,
    };
  }

  /* GETTERS */
  get isUploaded(): boolean {
    return !!this.id;
  }


  public async upload(storeInDatabase: boolean = true ): Promise<void> {
    // Upload represented file to storage
    try {
      // Get signed S3 URL
      const { url, key, created_at, acl } = await this.storage_service.create(this.toData()) as any;

      if (!(url && key && created_at)) {
        console.error("Failed to created signed URL.");
        return;
      }
      this.key = key;
      this.created_at = new Date(created_at);
      
      // PUT the file to the signed S3 URL
      const options: AxiosRequestConfig = {
        headers: {
          'Content-Type': this.mime,
          'x-amz-acl': acl
        },
        onUploadProgress: (progressEvent) => this.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
      };
      
      this.uploading = true;
      await axios.put(url, this.uri, options);

      this.uploading = false;
    } catch (error) {
      console.error('Error while uploading file to storage', error);
    }

    if (!this.key) {
      console.error("Failed to upload file.")
      return;
    }

    if( storeInDatabase ) {
      await super.save(); // Save to File table
  
      if (!this.id) {
        console.error("Failed to create database object. Rolling back");
        try {
          this.storage_service.remove(this.key);
          this.key = null;
        } catch (err) {
          console.error("Failed clean up file. Potentially orphaned data with key ", this.key);
        }
      }

    }
    return;
  }

  public getURL = async (): Promise<string> => {
    const res: {url: string} = await this.storage_service.get(this.key);
    return res.url;
  }

  public openInWindow = async (): Promise<void> => {
    const url = await this.getURL();
    window.open(url, '_blank').focus();
  }

  public copyToClipboard = async (): Promise<void> => {
    const url = await this.getURL();
    window.open(url, '_blank').focus();
  }

  public get getProgress(): number {
    return this.progress;
  }

  public get isUploading(): boolean {
    return this.uploading;
  }

  public get uploadedBy(): string {
    return rootStore.userStore.getItemByID(this.created_by) ? 
      rootStore.userStore.getItemByID(this.created_by).getFullName
      : null;
  }
}





export class AvatarImage extends File {

  protected constructor(data: IFile) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(data);

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    // this.fromData(data);
    this.storage_service = client.service(Services.Avatar);
  }


  public static create(data?: IFile): AvatarImage {
    const fileData = {
      ...data,
      key: data.key,
      uri: data.uri,
      title: data.title || null,
      type: data.type || null,
      size: data.size || null,
      ...Data.create(data),
    };
    return new AvatarImage(fileData);
  }

  public async upload(): Promise<void> {
    // Upload represented file to storage
    try {

      // Get signed S3 URL
      const { url, key, acl } = await this.storage_service.create(this.toData()) as any;

      if (!(url && key)) {
        console.error("Failed to created signed URL.");
        return;
      }

      this.key = key;
      
      // PUT the file to the signed S3 URL
      const options: AxiosRequestConfig = {
        headers: {
          'Content-Type': this.mime,
          'x-amz-acl': acl
        },
        onUploadProgress: (progressEvent) => this.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
      };
      
      this.uploading = true;
      await axios.put(url, this.uri, options);

      this.uploading = false;
    } catch (error) {
      console.error('Error while uploading avatar to storage', error);
    }

    if (!this.key) {
      console.error("Failed to upload avatar.")
      return;
    }
    return;
  }

}